import * as React from 'react';
import { Icon, LabelButton } from '@teamsnap/snap-ui';

type Props = {
  filename?: string;
  originalFileName?: string;
  file: File;
  setFile: (x: File) => void;
  error: string;
  setError: (x: string) => void;
};

const allowedFileTypes = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const FileUpload = ({ filename, originalFileName, file, setFile, error, setError }: Props) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const linkRef = React.useRef<HTMLAnchorElement | null>(null);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (file.size / 1024 > 20460) {
      return setError('File size must be 20MB or less');
    }
    if (!allowedFileTypes.includes(file.type)) {
      return setError('Please upload a JPEG, PNG, DOC, or PDF only.');
    }

    setError('');
    setFile(file);
  };

  const onAttachFileClick = () => {
    if (file) {
      setFile(undefined);
      fileInputRef.current.value = '';
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const removeFile = () => {
    setFile(undefined);
    fileInputRef.current.value = '';
  };

  React.useEffect(() => {
    if (file && file.size > 0 && file.name !== filename) {
      const objectURL = URL.createObjectURL(file);
      setDownloadUrl(objectURL);
    }
    return () => {
      setDownloadUrl(null);
    };
  }, [file, filename]);

  return (
    <>
      <input
        type="file"
        accept=".pdf, image/png, image/jpeg, .doc, .docx"
        onChange={handleFileChange}
        className="sui-hidden"
        ref={fileInputRef}
      />
      <div>
        {file && !error && (
          <div className="sui-mb-1">
            <div className="sui-inline-flex sui-items-center sui-bg-neutral-background-medium sui-pl-1 sui-pr-0.5 sui-py-1 sui-leading-none sui-rounded">
              {downloadUrl ? (
                <a
                  ref={linkRef}
                  className="sui-underline sui-pl-0.5 sui-text-action-text"
                  href={downloadUrl}
                  download={file.name}
                >
                  {originalFileName ?? file.name}
                </a>
              ) : (
                <span className="sui-pl-0.5 sui-text-neutral-text">{originalFileName ?? file.name}</span>
              )}

              <button
                className="sui-p-0 sui-border-none sui-min-w-0 sui-ml-0.5 sui-leading-none sui-mt-px sui-cursor-pointer"
                onClick={removeFile}
              >
                <Icon name="close" size="s" />
              </button>
            </div>
          </div>
        )}
        {error && (
          <div className="sui-mb-1">
            <div className="sui-inline-flex sui-items-center sui-bg-neutral-background-medium sui-pl-1 sui-pr-0.5 sui-py-1 sui-leading-none sui-rounded">
              <span className="sui-pl-0.5 sui-text-neutral-text-medium">{file.name}</span>
              <span className="sui-inline-flex sui-items-center sui-text-negative-text sui-pl-0.5 sui-caption">
                <Icon name="report" size="s" className="sui-pr-0.5" />
                File upload failed.
              </span>
              <button
                className="sui-p-0 sui-border-none sui-min-w-0 sui-ml-0.5 sui-leading-none sui-mt-px sui-cursor-pointer"
                onClick={removeFile}
              >
                <Icon name="close" size="s" />
              </button>
            </div>
          </div>
        )}
        <LabelButton size="small" variantType="secondary" onClick={onAttachFileClick} disabled={file}>
          <span>Attach File</span>
        </LabelButton>
      </div>
    </>
  );
};

export default FileUpload;
